/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import DS from 'ember-data';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import { dropTask, task } from 'ember-concurrency';
import { tracked } from '@glimmer/tracking';
import AlertsService from 'cio/services/alerts';
import SubscriptionTopicModel from 'cio/models/subscription-topic';
import AnalyticsService from 'cio/services/analytics';
import SubscriptionCenterService from 'cio/services/subscription-center';
import { bool } from '@ember/object/computed';
import AccountService from 'cio/services/account';
import EnvironmentService from 'cio/services/environment';
import { queryEncode } from 'cio/helpers/query';
import getUnsubscribedFilters from 'cio/utils/get-unsubscribed-filters';
import { EventsType } from 'cio/utils/analytics/events';

export default class TopicManagement extends Component<never> {
  @service account!: AccountService;
  @service alerts!: AlertsService;
  @service analytics!: AnalyticsService;
  @service environment!: EnvironmentService;
  @service store!: DS.Store;
  @service subscriptionCenter!: SubscriptionCenterService;

  @tracked recordToDelete!: SubscriptionTopicModel | null;
  @tracked newTopic: SubscriptionTopicModel | null = null;
  @tracked showEnableCenterModal = false;

  subCenterPreviewImage = new URL(
    'cio/assets/images/subscription-center/sub-center-preview.png',
    import.meta.url,
  ).toString();

  @bool('newTopic') isAdding!: boolean;

  constructor(owner: unknown, args: never) {
    super(owner, args);

    this.initializeData.perform();
  }

  get subscriptionCenterEnabled() {
    return this.subscriptionCenter.enabled;
  }

  saveRow = task(async (topic: SubscriptionTopicModel) => {
    try {
      await topic.save();

      this.alerts.success('subscriptionTopic.update.success', 'Topic saved');

      if (this.newTopic === topic) {
        this.newTopic = null;
        await this.subscriptionCenter.initializeTask.perform({ forceRefresh: true });
      }
    } catch (error) {
      this.alerts.error('subscriptionTopic.update.error', 'There was a problem saving your topic. Please try again.');
    }
  });

  @action
  createNewTopic() {
    this.newTopic = this.store.createRecord('subscription-topic');
    this.analytics.track('subscription_center', 'add_topic_started');
  }

  @action
  createCancel() {
    if (this.newTopic == null) {
      return;
    }

    this.newTopic.rollbackAttributes();
    this.newTopic = null;
  }

  initializeData = dropTask(async () => {
    await this.subscriptionCenter.initializeTask.perform(true);
    await this.fetchSubscribedCustomersTask.perform();
  });

  fetchSubscribedCustomersTask = task(async () => {
    let filters = queryEncode(getUnsubscribedFilters(null, false) as Record<string, any>);
    return await this.store.query('customer', {
      filters,
    });
  });

  @action
  trackLink(event: keyof EventsType['subscription_center']) {
    this.analytics.track('subscription_center', event);
  }

  @action
  toggleShowEnableCenterModal(show: boolean) {
    this.showEnableCenterModal = show;
  }
}
